import { Button, Flex, LoadingOrbit, Text } from '@chatsdet/ui';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';

type TemplateStatusPanelsProps = {
  template: EnvironmentTemplate;
  isStartingAuthoring: boolean;
  isRegenerating: boolean;
  onStartAuthoring: (mode: 'author' | 'explore') => void;
  onRegenerate: () => void;
};

export function TemplateStatusPanels({
  template,
  isStartingAuthoring,
  isRegenerating,
  onStartAuthoring,
  onRegenerate,
}: TemplateStatusPanelsProps) {
  return (
    <>
      {template.status === 'authoring' && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-card">
          <Flex align="center" justify="between">
            <div>
              <Text weight="medium">Sandbox is ready</Text>
              <Text size="sm" className="text-muted-foreground mt-1">
                Explore the code or create a template with AI-assisted authoring.
              </Text>
            </div>
            <Flex gap="sm">
              <Button
                variant="outline"
                onPress={() => onStartAuthoring('explore')}
                isDisabled={isStartingAuthoring}
              >
                {isStartingAuthoring ? 'Starting...' : 'Explore Code'}
              </Button>
              <Button onPress={() => onStartAuthoring('author')} isDisabled={isStartingAuthoring}>
                {isStartingAuthoring ? 'Starting...' : 'Create Template'}
              </Button>
            </Flex>
          </Flex>
        </div>
      )}

      {template.status === 'failed' && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-card">
          <Flex align="center" justify="between">
            <div>
              <Text weight="medium">Template authoring failed</Text>
              <Text size="sm" className="text-muted-foreground mt-1">
                {template.verificationLogs ||
                  'The previous authoring attempt failed. Regenerate to start over.'}
              </Text>
            </div>
            <Button onPress={onRegenerate} isDisabled={isRegenerating}>
              {isRegenerating ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </Flex>
        </div>
      )}

      {template.status === 'provisioning' && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-card">
          <Flex align="center" gap="md">
            <LoadingOrbit size="sm" />
            <div>
              <Text weight="medium">Provisioning sandbox...</Text>
              <Text size="sm" className="text-muted-foreground mt-1">
                Setting up the sandbox environment. This usually takes 2-3 minutes.
              </Text>
            </div>
          </Flex>
        </div>
      )}
    </>
  );
}
